import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { FunnelBars, RankedBars, StageBoard } from '@/shared/ui/charts'
import {
  CollectedAt, LabeledBar, MomentumBadge, SideBadge,
} from '@/features/dossier/components/primitives'
import { getKillChain, type KillChainColumn, type Target } from './api'
import { STAGE_GATE, stageLabel } from './stages'

/**
 * What each gate actually checks, shown on the column so nobody has to guess.
 *
 * Shared with the move panel — see ./stages — so the hint on the column and the requirement
 * on the button are the same sentence.
 */
const GATE_HINT = STAGE_GATE

/**
 * The column heads read from the CODE, never from `column.label`.
 *
 * The server's label is the stored enum prettified — "Weaponize", "Neutralize" — so a page
 * that renders it undoes the renaming in ./stages on every surface at once. That is exactly
 * what happened: the heading above the board said "Research in progress" while the columns
 * underneath it still said RECON and DEPLOYED.
 */
const columnLabel = (stageCode: string) => stageLabel(stageCode)

/**
 * The five-stage board.
 *
 * ## The gate hints are on the columns on purpose
 *
 * The stage names are PollSphere's; what makes this version deployable is that each move
 * must be earned. Somebody looking at a target stuck in RECON should be able to see what it
 * needs without opening documentation — otherwise the gates read as arbitrary refusals and
 * the first support request is "why won't it let me".
 */
export function KillChainPage() {
  const navigate = useNavigate()
  const [columns, setColumns] = useState<KillChainColumn[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setColumns(await getKillChain())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !columns) return <ErrorStrip message={error} />
  if (!columns) return <LoadingBlock label="Loading the board" />

  const total = columns.reduce((n, c) => n + c.targets.length, 0)
  const reached = (index: number) =>
    columns.slice(index).reduce((n, c) => n + c.targets.length, 0)
  const neutralised = columns.find((c) => c.stageCode === 'NEUTRALIZE')?.targets.length ?? 0
  const unscored = columns.flatMap((c) => c.targets).filter((t) => t.threatScore == null).length

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Research in progress"
        description="Where each opponent file has got to, and what the next step needs before
                     it can be taken. Nothing moves forward without the evidence for it, and
                     what was checked is kept — which is what a claim challenged in public
                     six months later rests on."
      />

      <KpiStrip>
        <MetricCard label="Targets" value={total} sub="Across every stage" />
        <MetricCard label="Past research" value={total - (columns[0]?.targets.length ?? 0)}
                    sub="Files with enough evidence to have moved on" />
        <MetricCard label="Closed out" value={neutralised}
                    sub="Finished, with the outcome written down" />
        <MetricCard label="Not scored" value={unscored}
                    sub="No publishable estimate — never read as zero" />
      </KpiStrip>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">How far the field has got</p>
            <p className="text-[10px] text-muted-foreground">
              {/* CUMULATIVE, not a count per column: a target sitting at Deploy has passed
                  research and building the case, and a funnel that forgot that would read as attrition
                  where there is none. */}
              Each bar counts every target that has reached that stage or gone past it.
            </p>
            <FunnelBars
              className="mt-2"
              // A file sitting at Research has not been LOST; it is being worked on. The
              // default wording ("did not continue") reads as a team losing opponent files.
              dropLabel={(n, pct) => `${n} (${pct}%) have not got this far yet`}
              holdLabel="every file got this far"
              stages={columns.map((c, i) => ({
                label: columnLabel(c.stageCode),
                value: reached(i),
                hint: GATE_HINT[c.stageCode],
              }))}
            />
          </div>
        </Panel>

        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">Where each gate is holding people up</p>
            <p className="text-[10px] text-muted-foreground">
              A tall bar is not a problem in itself — it is where the work is.
            </p>
            <RankedBars
              className="mt-2"
              points={columns.map((c) => ({
                label: columnLabel(c.stageCode), value: c.targets.length,
              }))}
              emptyMessage="No targets open yet."
            />
            <div className="mt-3 space-y-2">
              {columns.map((c) => (
                <LabeledBar
                  key={c.stageCode}
                  label={`${columnLabel(c.stageCode)} — ${c.targets.length} of ${total || 1}`}
                  value={total === 0 ? 0 : Math.round((c.targets.length / total) * 100)}
                  tone={c.stageCode === 'NEUTRALIZE' ? 'bg-emerald-500' : undefined}
                />
              ))}
            </div>
          </div>
        </Panel>
      </div>

      <Panel>
        <div className="p-2">
          <StageBoard<Target>
            showHints
            columns={columns.map((c) => ({
              key: c.stageCode,
              label: columnLabel(c.stageCode),
              hint: GATE_HINT[c.stageCode],
              items: c.targets,
            }))}
            renderItem={(t) => (
              <button
                type="button"
                onClick={() => navigate(`/opposition/targets/${t.id}`)}
                className="w-full rounded border border-border bg-background p-1.5 text-left
                           hover:bg-muted/50"
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span className="truncate text-[12px] font-medium">{t.displayName}</span>
                  <span className="shrink-0 text-[11px] tabular-nums">
                    {t.threatScore == null ? '—' : Math.round(t.threatScore)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <SideBadge side={t.sideCode} />
                  <MomentumBadge momentum={t.momentumCode} />
                </div>
                <CollectedAt at={t.scoresAsOf} prefix="scored" className="mt-0.5 block" />
              </button>
            )}
          />
        </div>
      </Panel>
    </div>
  )
}
